import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { Button, Card, Col, Row, Typography, Tag, Flex, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'
import { getManagersByStyle } from '@/api'
import { useFeedback } from '@/utils/antdApp'
import type { ManagerDetail } from '@/types'
import { colors , groupColors} from '../static'
import { filterColor } from '@/utils/filters'
import './index.scss'

const { Text } = Typography

function ManagerCard ({
    parentColor,
    manager, 
    index , 
    idx,
    onDetail , 
    onEdit
}: {
    parentColor: string,
    manager: ManagerDetail,
    index: number,
    idx: number,
     onDetail: (id: number, idx: number, index: number) => void, 
     onEdit: (id: number) => void,
}) {
    const getRiskLevelColor = (riskLevel?: string) => {
        let color = 'accent'
        if (!riskLevel) {
            color = 'accent'
        }
        if(riskLevel?.includes('低')) {
            color = 'green'
        }else if(riskLevel?.includes('中')) {
            color = 'warning'
        }else if(riskLevel?.includes('高')) {
            color = 'error'
        }
        return `is-${color}`
    }

    return (
        <Card
            key={manager.id}
            className="manager-page-card"
            style={{height: '100%', '--manager-color': colors[idx % colors.length] , '--group-color': parentColor } as CSSProperties}
            size="small"
            onClick={() => onDetail(manager.id ?? 0, idx, index)}
        >
            <div className='manager-title'>
                <div className='avatar flex flex-center'>{manager.initials}</div>
                <div className=''>
                    <Text style={{ fontSize: 16, fontWeight: 700 }}>{manager.nickname ?? '--'}</Text>
                    <div className='manager-title-tags'>
                        <Tag variant='filled' className='text-10 is-accent'>{manager.style}</Tag>
                        <Tag variant='filled' className={`text-10 ${getRiskLevelColor(manager.risk_level)}`} style={{margin: '0 4px'}}>{manager.risk_level ?? '--'}</Tag>
                        <span style={{ fontSize: 11, color: '#64748b' }}>进化 {manager.evolution_count ?? '--'} 次</span>
                    </div>
                </div>
            </div>
            <div className='manager-content'>
                <span>
                    {manager.strategy.trim().slice(0, 40).replace(/\n/g, ' ')}
                    {manager.strategy.trim().length > 60 ? '…' : ''}
                </span>
                <div className='manager-content-details'>
                    <Flex className='manager-content-tags' gap="4px" align="center" wrap>
                        {manager.focus_sectors.map((ele) => (
                            <Tag key={ele} className='label-tag text-10' style={{ color: '#94a3b8' }} variant='outlined'>
                                {ele}
                            </Tag>
                        ))}
                    </Flex>
                    <Row className='manager-content-details-row'>
                        <Col span={6} className='manager-content-details-item'>
                            <div>智能组合</div>
                            <Text style={{ fontWeight: 700 }}>{manager.portfolios_count ?? '--'} 个</Text>
                        </Col>
                        <Col span={6} className='manager-content-details-item'>
                            <div>年化收益</div>
                            <Text style={{ fontWeight: 700 }}>{manager.annual_return ?? '--'}%</Text>
                        </Col>
                        <Col span={6} className='manager-content-details-item'>
                            <div>夏普比率</div>
                            <Text style={{ fontWeight: 700 }}>{manager.sharpe_ratio ?? '--'}</Text>
                        </Col>
                        <Col span={6} className='manager-content-details-item'>
                            <div>最大回撤</div>
                            <Text style={{ fontWeight: 700 }} color={filterColor(manager.max_drawdown)} >{manager.max_drawdown ?? '--'}%</Text>
                        </Col>
                    </Row>
                </div>
                <div className='manager-content-footer flex flex-between'>
                    管理资产 {manager.total_assets ?? '--'} 万 · 胜率 {manager.win_rate ?? '--'} %
                    <div className='manager-content-footer-button'>
                        <Button size='small' style={{ marginRight: 10,fontSize: 10 }} onClick={(e) => {
                            e.stopPropagation()
                            onEdit(manager.id ?? 0)
                        }}>
                            修改
                        </Button>
                        <Button type='primary' size='small' style={{fontSize: 10 }} onClick={(e) => {
                            e.stopPropagation()
                            onDetail(manager.id ?? 0, idx, index)
                        }}>
                            查看详情
                        </Button>
                    </div>
                </div>
            </div>
        </Card>
    )
}

export default function ManagersPage() {
    const { message } = useFeedback()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [managersCount, setManagersCount] = useState(0)
    const [managerItems, setManagerItems] = useState<{style: string,count:number, managers: ManagerDetail[]}[]>([])

    const load = () => getManagersByStyle().then(data => {
        setManagerItems(data)
        setManagersCount(data.reduce((acc, item) => acc + item.count, 0))
    }).catch(() => message.error('加载失败')).finally(() => setLoading(false))

    useEffect(() => {
        load()
    }, [])

    const onDetail = (id: number, idx: number, index: number) => {
        navigate(`/managers/${id}?idx=${idx}&index=${index}`)
    }

    const onEdit = (id: number) => {
        navigate(`/managers/operate/${id}`)
    }

    return (
        <div className='managers-page'>
            <div className='page-header flex flex-between'>
                <div className='managers-page-header-title'>
                    <span className='color-white'>智能组合经理</span>
                    <div style={{ fontSize: 11, marginTop: 8 }}>AI驱动的投资组合管理智能体 · {managersCount} 位经理</div>
                </div>
                <Button type="primary" onClick={() => navigate('/managers/operate')}>
                    <Text style={{ fontSize: 16 }}>+</Text> 创建组合经理
                </Button>
            </div>
            {loading ? 
                <div className='page-loading'><Spin spinning={loading} /></div> : (
                <div className='page-content'>
                    <div className='managers-page-list'>
                        {managerItems.map((m, index) => (
                            <div className='managers-page-card-list' key={m.style}>
                                <div className='managers-page-card-list-item-title text-10' style={{ '--group-color': groupColors[index % colors.length] } as CSSProperties} >
                                    <Tag className='text-12 managers-page-card-list-style-tag' style={{backgroundColor: 'transparent'}}>{m.style}</Tag>
                                    {m.count} 位
                                </div>
                                <div className='managers-page-card-list-item'>
                                    {m.managers.map((manager, idx) => (
                                        <ManagerCard 
                                            key={manager.id}
                                            manager={manager} 
                                            index={index} 
                                            idx={idx}
                                            onDetail={onDetail} 
                                            onEdit={onEdit} 
                                            parentColor={groupColors[index % colors.length]} 
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
